import _extends from "@babel/runtime/helpers/extends";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _class2, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _ArgumentOperandValueEditor;
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { InlinePanelLayout } from "abreact/expression/layout/InlinePanelLayout";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { OperandValueEditor } from "abreact/expression/ui/editor/impl/condition/operandValue/operand/OperandValueEditor";
import { ArgumentOperandIdentityImpl } from "abreact/expression/ui/editor/impl/factory/ArgumentOperandIdentityImpl";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbBooleanLogicTreeFocus } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeFocus";
import { useComponentWithManagedFocus } from "abreact/incubator/ui/abBooleanLogicTree/AbGenericFocusManager";
import { ForwardFocusRef } from "abreact/incubator/ui/utils/FocusUtils";
import { AbMultiplexInput } from "abreact/input/AbMultiplexInput";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbHBox } from "abreact/layout/AbHBox";
import { AbLabel } from "abreact/misc/AbLabel";
import { AbBusyIndicator } from "abreact/progress/AbBusyIndicator";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { Assert } from "abweb/utils/Assert";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { useMemo } from "react";
const noOp = () => {
  // Nothing.
};

/**
 * @author fgracely
 * This represents an argument with all of its possible input options.
 */
export let ArgumentOperandValueEditor = (_dec = observable.ref, _dec2 = observable.ref, _dec3 = observable.ref, _dec4 = observable.ref, _dec5 = action.bound, _dec6 = action.bound, observer(_class = (_class2 = (_ArgumentOperandValueEditor = class ArgumentOperandValueEditor extends React.Component {
  constructor(props) {
    super(props);
    _initializerDefineProperty(this, "_availableArgumentOperandIdentities", _descriptor, this);
    _initializerDefineProperty(this, "_argumentOperandIdentity", _descriptor2, this);
    _initializerDefineProperty(this, "_editorProviderMap", _descriptor3, this);
    _initializerDefineProperty(this, "_dragDropEditorMachine", _descriptor4, this);
    _initializerDefineProperty(this, "_isInitializationPending", _descriptor5, this);
    _defineProperty(this, "potentialDependenciesOperandNames", void 0);
    _defineProperty(this, "_initializationTriggerDisposers", []);
    _defineProperty(this, "_forwardFocusRef", void 0);
    this.applyOperandValue = this.applyOperandValue.bind(this);
    this.initialize = this.initialize.bind(this);
    this.renderOperandValueEditor = this.renderOperandValueEditor.bind(this);
    this.createArgumentOperandValueEditorProps = this.createArgumentOperandValueEditorProps.bind(this);
    this._forwardFocusRef = props.managedFocusRef ?? new ForwardFocusRef();
    this.potentialDependenciesOperandNames = this.computePotentialDependencies();
    makeObservable(this);
  }
  computePotentialDependencies() {
    const operandNames = new Set();
    const groupIds = new Set();
    const processDep = dep => {
      if (dep.groupID != null) groupIds.add(dep.groupID);
      if (dep.operandName != null) operandNames.add(dep.operandName);
    };
    // Add all the dependencies for the group
    this.props.argumentOperand.expArgumentOperandGroup?.dependencies?.forEach(processDep);
    // Add all dependencies for all potential operand instances
    this.props.argumentOperand.expArgumentOperands.forEach(operand => {
      operand.dependencies?.forEach(processDep);
    });
    // Now expand the group dependencies
    this.props.operatorOperandValue.expOperator.argumentOperands?.forEach(arg => {
      if (arg.groupID != null && groupIds.has(arg.groupID)) {
        operandNames.add(arg.name);
      }
    });
    return Array.from(operandNames).sort();
  }
  get dataTestId() {
    let dataTestId = `${this.props.argumentOperand.argumentIdentifier}-argument`;
    if (this.props.dataTestId) {
      dataTestId = `${this.props.dataTestId}-${dataTestId}`;
    }
    return dataTestId;
  }
  get valueCategoryID() {
    return this._argumentOperandIdentity?.valueCategoryID;
  }
  render() {
    if (this._argumentOperandIdentity == null) {
      return null;
    }
    const baseProps = {
      renderOperandValueEditor: this.renderOperandValueEditor,
      operandValue: this.props.operandValue,
      valueCategoryID: this.valueCategoryID,
      operandValueContext: {
        isSourceOperandValue: false,
        sourceOperandValue: this.props.sourceOperandValue,
        argumentOperandIdentity: this._argumentOperandIdentity
      },
      operatorOperandValue: this.props.operatorOperandValue,
      liveViewSliceModel: this.props.liveViewSliceModel,
      componentFactory: this.props.componentFactory,
      isBusy: this._isInitializationPending,
      dataTestId: this.dataTestId
    };
    if (this.props.isReadonly) {
      return /*#__PURE__*/React.createElement(OperandValueEditor, _extends({
        isReadonly: true
      }, baseProps));
    } else {
      return /*#__PURE__*/React.createElement(OperandValueEditor, _extends({
        isReadonly: false
      }, baseProps, {
        dragDropEditorMachine: this._dragDropEditorMachine,
        generateId: this.props.generateId,
        applyOperandValue: this.applyOperandValue
      }));
    }
  }
  applyOperandValue(operandValueMaybePromise) {
    Assert.assert(!this.props.isReadonly);
    if (!this._isInitializationPending) {
      Assert.assert(this._argumentOperandIdentity != null);
      this.props.applyOperandValue(this._argumentOperandIdentity.operandName, operandValueMaybePromise);
    }
  }
  componentDidMount() {
    this.registerInitializationTriggers();
  }
  componentWillUnmount() {
    this.disposeInitializationTriggers();
  }
  componentDidUpdate(prevProps) {
    if (this.props.argumentOperand !== prevProps.argumentOperand) {
      this.potentialDependenciesOperandNames = this.computePotentialDependencies();
    }
  }
  registerInitializationTriggers() {
    /**
     * React to changes in priors (source, operator and argument dependencies).
     */
    this._initializationTriggerDisposers.push(reaction(() => {
      const {
        sourceOperandValue,
        operatorOperandValue
      } = this.props;
      const dependencies = this.potentialDependenciesOperandNames.map(depName => this.props.argumentOperandValuesByOperandName.get(depName));
      return {
        operatorOperandValue,
        operands: [sourceOperandValue, ...dependencies]
      };
    }, this.initialize, {
      fireImmediately: true,
      equals: (a, b) => {
        return ArrayUtils.isEquals(a.operands, b.operands, (ai, bi) => GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.equals(ai, bi)) && a.operatorOperandValue.expOperator.id === b.operatorOperandValue.expOperator.id;
      }
    }));
    /**
     * React to self (specifically, if a dropped operand value has a different value category than current selection).
     */
    this._initializationTriggerDisposers.push(reaction(() => this.props.operandValue, operandValue => {
      if (GuidedExpressionEditorUtils.isValueCategoryStale(operandValue, this.valueCategoryID)) {
        return this.initialize();
      }
    }, {
      fireImmediately: true,
      equals: GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.equals
    }));
  }
  disposeInitializationTriggers() {
    this._initializationTriggerDisposers.forEach(disposer => disposer());
  }
  async initialize() {
    runInAction(() => this._isInitializationPending = true);
    const {
      validArgumentOperandIdentities
    } = this;
    const editorProviders = await this.props.componentFactory.getArgumentOperandValueEditorProviders(validArgumentOperandIdentities.map(this.createArgumentOperandValueEditorProps));
    const editorProviderMap = new Map(editorProviders.map(editorProvider => {
      return [editorProvider.argumentOperandIdentity.key.toString(), editorProvider];
    }));
    if (editorProviderMap.size === 0) {
      runInAction(() => {
        this._availableArgumentOperandIdentities = [];
        this._argumentOperandIdentity = undefined;
        this._editorProviderMap = editorProviderMap;
      });
    } else {
      /**
       * Now that we've determined which possible categories have editors associated with them, calculate the available
       * categories. Some categories may have dropped out due to there being no applicable data. For instance -- no
       * objects within constraints for an instance selector.
       */
      const availableArgumentOperandIdentities = this.computeAvailableArgumentOperandIdentities(validArgumentOperandIdentities, editorProviderMap);
      const argumentOperandIdentity = await this.computeArgumentOperandIdentity(availableArgumentOperandIdentities, editorProviderMap);
      runInAction(() => {
        this._availableArgumentOperandIdentities = availableArgumentOperandIdentities.sort(Expression.Domain.ArgumentOperand.Identity.compare);
        this._argumentOperandIdentity = argumentOperandIdentity;
        this._editorProviderMap = editorProviderMap;
      });
    }
    runInAction(() => this._isInitializationPending = false);
  }
  get validArgumentOperandIdentities() {
    const validArgumentOperandIdentities = [];

    /*
     * The options will include all possible operators that can be used as functions. Only want to include function in
     * the dropdown once, not once for each operator.
     */
    let functionAdded = false;
    let formulaAdded = false;
    for (const elementInfo of this.props.argumentOperand.elementInfos) {
      const {
        elementOption
      } = elementInfo;
      let expValueCategory;
      let expValueType;
      if (elementOption.isOperatorOption()) {
        if (functionAdded) {
          continue;
        }
        expValueCategory = this.props.expressionDomainModel.getExpValueCategory(elementOption.valueCategoryID);
        expValueType = this.props.expressionDomainModel.getExpValueType(elementOption.valueTypeID);
        functionAdded = true;
      } else if (elementOption.isFormulaOption()) {
        if (formulaAdded) {
          continue;
        }
        expValueCategory = elementOption.expValueCategory;
        expValueType = elementOption.expValueType;
        formulaAdded = true;
      } else if (elementOption.isValueOption()) {
        expValueCategory = elementOption.expValueCategory;
        expValueType = elementOption.expValueType;
      } else {
        throw new Error(`Unrecognized element option type: ${elementOption.type}`);
      }
      const argumentOperandIdentity = new ArgumentOperandIdentityImpl(expValueCategory, expValueType, elementOption.multiple, this.props.operatorOperandValue.expOperator, elementInfo.expArgumentOperand, this.props.argumentOperand.expArgumentOperandGroup, elementOption.isOperatorOption());
      validArgumentOperandIdentities.push(argumentOperandIdentity);
    }
    return validArgumentOperandIdentities;
  }
  computeAvailableArgumentOperandIdentities(validArgumentOperandIdentities, editorProviderMap) {
    let availableArgumentOperandIdentities = validArgumentOperandIdentities.filter(argumentOperandIdentity => {
      return editorProviderMap.has(argumentOperandIdentity.key.toString());
    });
    if (availableArgumentOperandIdentities.length > 1) {
      const distinctIdentityCategoryLabels = new Set();
      const distinctAvailableArgumentOperandIdentities = [];
      /*
      If the list includes two options, that are the same (which can happen with overloaded operators
      that infer their type from Source Values with multiple Value Types), such as
      [Reference, Reference, Literal, Literal], only include the first, which will match the first value type specified
      for which an input control was provided.
       */
      for (const argumentOperandIdentity of availableArgumentOperandIdentities) {
        if (!distinctIdentityCategoryLabels.has(argumentOperandIdentity.label)) {
          distinctIdentityCategoryLabels.add(argumentOperandIdentity.label);
          distinctAvailableArgumentOperandIdentities.push(argumentOperandIdentity);
        }
      }
      availableArgumentOperandIdentities = distinctAvailableArgumentOperandIdentities;
    }
    return availableArgumentOperandIdentities.sort(Expression.Domain.ArgumentOperand.Identity.compare);
  }
  async computeArgumentOperandIdentity(availableArgumentOperandIdentities, editorProviderMap) {
    let resolvedArgumentOperandIdentity;
    // Now that we know what possible categories exist, find the current selection within them
    for (let argumentOperandIdentity of availableArgumentOperandIdentities) {
      if (this.props.operandValue != null) {
        if (argumentOperandIdentity.represents(this.props.operandValue)) {
          resolvedArgumentOperandIdentity = argumentOperandIdentity;
          break;
        }
      }
    }
    // If there was no identity matching the operand value, try matching the currently selected identity's category label.
    // (The label is the user-visible dropdown selection, which is what we want to retain)
    if (resolvedArgumentOperandIdentity == null) {
      for (let argumentOperandIdentity of availableArgumentOperandIdentities) {
        if (this._argumentOperandIdentity != null && argumentOperandIdentity.label === this._argumentOperandIdentity.label) {
          resolvedArgumentOperandIdentity = argumentOperandIdentity;
          break;
        }
      }
    }
    // If there was no current selection found, then find a good default
    if (resolvedArgumentOperandIdentity == null) {
      if (!this.props.isReadonly) {
        if (this.props.sourceOperandValue != null && this.props.customizationFunctions?.computeDefaultArgumentOperandIdentity != null) {
          resolvedArgumentOperandIdentity = this.props.customizationFunctions.computeDefaultArgumentOperandIdentity(this.props.sourceOperandValue, this.props.operatorOperandValue, availableArgumentOperandIdentities);
        }
      }
      if (resolvedArgumentOperandIdentity == null) {
        for (let argumentOperandIdentity of availableArgumentOperandIdentities) {
          if (argumentOperandIdentity.isPreferredDefault) {
            resolvedArgumentOperandIdentity = argumentOperandIdentity;
            break;
          }
        }
        if (resolvedArgumentOperandIdentity == null) {
          const empiricalArgumentOperandIdentity = await this.maybeResolveAndApplyEmpiricalArgumentOperandIdentity(availableArgumentOperandIdentities, editorProviderMap);
          // Just choose the first if there is none that is marked to be a default
          resolvedArgumentOperandIdentity = empiricalArgumentOperandIdentity ?? availableArgumentOperandIdentities[0];
        }
      }
    }
    return resolvedArgumentOperandIdentity;
  }
  async maybeResolveAndApplyEmpiricalArgumentOperandIdentity(availableArgumentOperandIdentities, editorProviderMap) {
    const {
      operandValue,
      argumentOperand,
      expressionDomainModel,
      componentFactory
    } = this.props;
    if (operandValue == null) {
      return undefined;
    }
    const {
      valueCategoryID,
      valueTypeIDs,
      multiple
    } = operandValue;
    if (valueCategoryID == null || valueTypeIDs?.size !== 1) {
      return undefined;
    }
    const expValueCategory = expressionDomainModel.getExpValueCategory(valueCategoryID);
    Assert.assert(valueTypeIDs.size === 1);
    const expValueType = expressionDomainModel.getExpValueType(valueTypeIDs.get(0));
    const expArgumentOperand = argumentOperand.expArgumentOperands.find(candidate => candidate.name === operandValue.operandName);
    if (expArgumentOperand == null) {
      return undefined;
    }
    const empiricalArgumentOperandIdentity = new ArgumentOperandIdentityImpl(expValueCategory, expValueType, multiple, this.props.operatorOperandValue.expOperator, expArgumentOperand, argumentOperand.expArgumentOperandGroup, OperatorOperandValueImpl.isInstance(operandValue) && !operandValue.isFormula);
    Assert.assert(empiricalArgumentOperandIdentity.represents(operandValue));
    const editorProviders = await componentFactory.getArgumentOperandValueEditorProviders([this.createArgumentOperandValueEditorProps(empiricalArgumentOperandIdentity)]);
    if (editorProviders.length === 0) {
      return undefined;
    }
    Assert.assert(editorProviders.length === 1);
    const [editorProvider] = editorProviders;
    Assert.assert(editorProvider.argumentOperandIdentity === empiricalArgumentOperandIdentity);
    let matchIndex;
    for (let index = 0; index < availableArgumentOperandIdentities.length; index++) {
      if (availableArgumentOperandIdentities[index].label === empiricalArgumentOperandIdentity.label) {
        matchIndex = index;
        break;
      }
    }
    if (matchIndex != null) {
      const [argumentOperandIdentity] = availableArgumentOperandIdentities.splice(matchIndex, 1, empiricalArgumentOperandIdentity);
      editorProviderMap.delete(argumentOperandIdentity.key.toString());
    } else {
      availableArgumentOperandIdentities.push(empiricalArgumentOperandIdentity);
    }
    editorProviderMap.set(editorProvider.argumentOperandIdentity.key.toString(), editorProvider);
    return empiricalArgumentOperandIdentity;
  }
  get editor() {
    Assert.assert(this._argumentOperandIdentity != null && this._editorProviderMap != null);
    const argumentOperandIdentityKey = this._argumentOperandIdentity.key.toString();
    const editorProvider = this._editorProviderMap.get(argumentOperandIdentityKey);
    Assert.assert(editorProvider != null, `Invalid key for editor: ${argumentOperandIdentityKey}`);
    const dependenciesMet = GuidedExpressionEditorUtils.Dependencies.areDependenciesMet(this._argumentOperandIdentity.expArgumentOperand, this._argumentOperandIdentity.expArgumentOperandGroup, GuidedExpressionEditorUtils.Dependencies.computeFilledArgumentOperandsForDependencyCheck(this._argumentOperandIdentity.expOperator, this.props.argumentOperandValuesByOperandName));
    const editor = !dependenciesMet ? /*#__PURE__*/React.createElement(AbTextInput, {
      className: "ab-gee-disabled-missing-dependency",
      value: "",
      disabled: true,
      onChange: noOp
    }) : editorProvider(this.createArgumentOperandValueEditorProps(editorProvider.argumentOperandIdentity));
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-gee-argument-operand-input-box",
      "data-test-id": `${this.dataTestId}-inputBox`,
      key: argumentOperandIdentityKey
    }, editor);
  }
  renderOperandValueEditor() {
    return /*#__PURE__*/React.createElement(AbHBox, {
      spacing: true,
      className: "operand-input-container"
    }, this.prefixLabelIfProvided, this.singleInputOrMultiplexInput, this.suffixLabelIfProvided);
  }
  get singleInputOrMultiplexInput() {
    if (this._isInitializationPending) {
      return /*#__PURE__*/React.createElement(AbBusyIndicator, null);
    }
    if (this._argumentOperandIdentity != null) {
      Assert.assert(this._availableArgumentOperandIdentities != null);
      if (this._availableArgumentOperandIdentities.length === 1) {
        return this.editor;
      }
      if (this._availableArgumentOperandIdentities.length > 1) {
        const argumentOperandIdentityKey = this._argumentOperandIdentity.key.toString();
        return /*#__PURE__*/React.createElement(AbMultiplexInput, {
          key: this.dataTestId,
          dataTestId: `${this.dataTestId}-MultiplexInput`,
          value: this._argumentOperandIdentity,
          items: this._availableArgumentOperandIdentities,
          itemRenderer: ({
            selectorLabel
          }) => selectorLabel,
          valueRenderer: ({
            label
          }) => label,
          disabled: this.props.isReadonly,
          onChange: this.onArgumentOperandIdentitySelected
        }, this._availableArgumentOperandIdentities.map(argumentOperandIdentity => {
          return /*#__PURE__*/React.createElement(React.Fragment, {
            key: argumentOperandIdentity.key.toString()
          }, argumentOperandIdentity.key.toString() === argumentOperandIdentityKey ? this.editor : null);
        }));
      }
    }
    return /*#__PURE__*/React.createElement("div", null, AbReactResources.expression_function_noInputAvailable());
  }
  onArgumentOperandIdentitySelected(argumentOperandIdentity) {
    Assert.assert(!this.props.isReadonly);
    if (this.context.expanded) {
      this.context.collapse();
    }
    if (this.props.operandValue != null) {
      const operandName = this.props.operandValue.operandName ?? argumentOperandIdentity.operandName;
      const operandValue = this.props.customizationFunctions?.coerceOperandValueToNewCategory?.(this.props.operandValue, this._argumentOperandIdentity?.valueCategoryID, argumentOperandIdentity.valueCategoryID);
      this.props.applyOperandValue(operandName, operandValue);
    }
    this._argumentOperandIdentity = argumentOperandIdentity;
  }
  createArgumentOperandValueEditorProps(argumentOperandIdentity) {
    const {
      operandName,
      valueCategoryID
    } = argumentOperandIdentity;
    // <hack>
    // This is a hack to avoid an exception in BRE code caused by a race-like condition:
    // In onArgumentOperandIdentitySelected above, this component's category gets modified and
    // the operand value gets applied. The application, however, is sometimes done behind
    // promises, and things can get out of sync. This ensures we don't try to render an
    // operand value with another category's editor.
    let operandValue = this.props.operandValue;
    if (!GuidedExpressionEditorUtils.operandValueMatchesCategoryForEditor(operandValue, valueCategoryID)) {
      operandValue = undefined;
    }
    // </hack>
    const baseProps = {
      operandName: operandName,
      operandValue: operandValue,
      expressionDomainModel: this.props.expressionDomainModel,
      liveViewSliceModel: this.props.liveViewSliceModel,
      dataTestId: this.dataTestId,
      displayProps: {
        debouncerActivityWindowMillis: this.props.componentFactory.debouncerActivityWindowMillis,
        showClearButtonIfSupported: this.props.componentFactory.getShowClearButtonIfSupported?.(valueCategoryID),
        forwardFocusRef: this._forwardFocusRef
      },
      generateId: this.props.generateId,
      argumentOperandIdentity: argumentOperandIdentity,
      sourceOperandValue: this.props.sourceOperandValue,
      operatorOperandValue: this.props.operatorOperandValue,
      argumentOperandValuesByOperandName: this.props.argumentOperandValuesByOperandName
    };
    if (this.props.isReadonly) {
      return {
        isReadonly: true,
        ...baseProps
      };
    } else {
      const {
        applyOperandValue
      } = this.props;
      return {
        isReadonly: false,
        ...baseProps,
        customizationFunctions: this.props.customizationFunctions,
        setDragDropEditorMachine: this.setDragDropEditorMachine,
        applyOperandValue: (operandValueMaybePromise, context) => {
          applyOperandValue(operandName, operandValueMaybePromise, context);
        }
      };
    }
  }
  setDragDropEditorMachine(dragDropEditorMachine) {
    this._dragDropEditorMachine = dragDropEditorMachine;
  }
  get prefixLabelIfProvided() {
    if (this._argumentOperandIdentity) {
      const prefixLabel = GuidedExpressionEditorUtils.getPrefixLabelIfProvided(this._argumentOperandIdentity.expArgumentOperand);
      if (prefixLabel) {
        return /*#__PURE__*/React.createElement(AbLabel, {
          label: prefixLabel
        });
      }
    }
  }
  get suffixLabelIfProvided() {
    if (this._argumentOperandIdentity) {
      const suffixLabel = GuidedExpressionEditorUtils.getSuffixLabelIfProvided(this._argumentOperandIdentity.expArgumentOperand);
      if (suffixLabel) {
        return /*#__PURE__*/React.createElement(AbLabel, {
          label: suffixLabel
        });
      }
    }
  }
}, _defineProperty(_ArgumentOperandValueEditor, "contextType", InlinePanelLayout.ExpansionContext), _ArgumentOperandValueEditor), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "_availableArgumentOperandIdentities", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "_argumentOperandIdentity", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "_editorProviderMap", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class2.prototype, "_dragDropEditorMachine", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class2.prototype, "_isInitializationPending", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class2.prototype, "dataTestId", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "dataTestId"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "valueCategoryID", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "valueCategoryID"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "editor", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "editor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "singleInputOrMultiplexInput", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "singleInputOrMultiplexInput"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onArgumentOperandIdentitySelected", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "onArgumentOperandIdentitySelected"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setDragDropEditorMachine", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "setDragDropEditorMachine"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "prefixLabelIfProvided", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "prefixLabelIfProvided"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "suffixLabelIfProvided", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "suffixLabelIfProvided"), _class2.prototype), _class2)) || _class);
export function ArgumentOperandValueEditorWithManagedFocus(props) {
  const {
    rowKey,
    argumentOperand
  } = props;
  const {
    argumentIdentifier
  } = argumentOperand;
  const targetRep = useMemo(() => new AbBooleanLogicTreeFocus.ArgTarget(rowKey ?? "", argumentIdentifier), [rowKey, argumentIdentifier]);
  const argumentOperandValueEditor = useComponentWithManagedFocus(ArgumentOperandValueEditor, targetRep);
  return argumentOperandValueEditor(props);
}
(function (_ArgumentOperandValueEditor2) {
  /**
   * See the description of {@link GuidedExpressionEditor.ArgumentOperand.Value.Editor.Props} for more information
   * about {@link BaseProps#sourceOperandValue} and {@link BaseProps#operatorOperandValue}.
   */

  function createKey(argumentOperand) {
    return argumentOperand.argumentIdentifier;
  }
  _ArgumentOperandValueEditor2.createKey = createKey;
})(ArgumentOperandValueEditor || (ArgumentOperandValueEditor = {}));
//# sourceMappingURL=ArgumentOperandValueEditor.js.map
